/**
 * Created by Alex on 9/08/2020.
 * Description:
 */
import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { AutoTagDialogService } from './autotag-model-dialog.service';
import { AutoTagModelDialogOpenOptions, AutoTagModelDialogType } from './autotag-model-dialog.model';
import { Store, select } from '@ngrx/store';
import { MultiSelDropdownFilterComponent } from '../../../../widgets/multi-sel-dropdown-filter/multi-sel-dropdown-filter.component';
import { AutoTaggingModel } from '../../../../tamalelibs/models/auto-tagging-model.model';
import { User } from '../../../../tamalelibs/models/user.model';
import { MultiSelDropdownFilterConfig } from '../../../../widgets/multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { AppState } from '../../../../redux';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { fullEntryTypeSelector } from '../../../../redux/reducers/entry-type.reducer';
import { CREATE_AUTO_TAG_MODEL, CREATE_AUTO_TAG_MODEL_SUCCESS } from '../../../../tamalelibs/redux/auto-tagging.actions';
import { ArrayHelperService } from '../../../../tamalelibs/services/array-helper.service';

@Component({
    selector: 'tam-autotag-model-dialog',
    templateUrl: './autotag-model-dialog.component.html',
    styleUrls: ['./autotag-model-dialog.component.scss']
})
export class AutoTagModelDialogComponent implements OnInit, OnDestroy {
    @ViewChild(MultiSelDropdownFilterComponent, { static: false }) multiSelDropdownEle: MultiSelDropdownFilterComponent;
    autoTaggingModel = new AutoTaggingModel();

    allNoteTypes = [];
    dialogOpened = false;
    dialogTitle: string;
    selectedNoteTypes: string;
    currentUser: User;
    dropdownConfig: MultiSelDropdownFilterConfig = new MultiSelDropdownFilterConfig();
    isSaving = false;
    errorMsg: string;
    loading = false;
    isModelNameEmpty = false;
    isModelNameExceedMaxlength = false;
    isNoteTypesEmpty = false;
    isNumberEmpty = false;
    isNumberExceedScope = false;

    private _destroySubscriptions: Subscription[] = [];

    constructor(
        private _service: AutoTagDialogService,
        private _store: Store<AppState>,
        private _alertWindow: AlertWindowService,
    ) { }

    ngOnInit() {
        this._initAutoTagModel();
        this._initSubscriptions();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    /*
    * check if there were any changes before cancel
    */
    cancelClick() {
        if (this.autoTaggingModel.modelName || this.autoTaggingModel.noteTypes || this.autoTaggingModel.number !== 200) {
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to discard all your changes?']).subscribe((result: boolean) => {
                    if (result) {
                        this._onClose();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._onClose();
        }
    }

    changeNumber(number) {
        // if the user delete the value, the number would be set to null
        this.autoTaggingModel.number = number;
    }

    onGenerateScript() {
        if (this._verifyInput()) {
            this._saveNewModel();
        }
    }

    private _getAllCheckedNoteTypeIds(checkedNoteTypes: Array<any>): string {
        const noteTypeIds = [];
        if (checkedNoteTypes) {
            checkedNoteTypes.forEach(item => {
                noteTypeIds.push(item.id);
            });
        }
        return noteTypeIds.length > 0 ? noteTypeIds.join(',') : '';
    }

    private _initAutoTagModel() {
        this.dialogTitle = 'Generate Training Script';
        this.autoTaggingModel.number = 200; // the default number is 200
    }

    private _initSubscriptions() {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),

            this._service.dialogOpen$.subscribe((_autoTagModelDialogOpenOptions: AutoTagModelDialogOpenOptions) => {
                if (_autoTagModelDialogOpenOptions.openType === AutoTagModelDialogType.CreateModel) {
                    this.dialogOpened = true;
                    this._resetDialog();
                }
            }),
            this._store.pipe(select(fullEntryTypeSelector))
                .subscribe(res => this._onSubscribeNoteTypesList(res)),
        );

        this.dropdownConfig.onChangeValue$.subscribe(data => {
            const value = typeof data === 'string' ? data : data.map(item => item.id);
            if (value === 'all') {
                const checkedNoteTypes = this.multiSelDropdownEle.getCheckedData();
                this.selectedNoteTypes = this._getAllCheckedNoteTypeIds(checkedNoteTypes);
            } else {
                this.selectedNoteTypes = (value) ? value.join(',') : '';
            }
            this.autoTaggingModel.noteTypes = this.selectedNoteTypes;
            this._verifyNoteTypes();
        });
    }

    private _isNoteTypeExisted(id: string) {
        if (this.autoTaggingModel.noteTypes.includes(id)) {
            return true;
        } else {
            return false;
        }
    }

    private _onClose() {
        this.dialogOpened = false;
    }

    /*
    * reset dialog after user click ceate new model
    */
    private _resetDialog() {
        this.isModelNameEmpty = false;
        this.isModelNameExceedMaxlength = false;
        this.isNumberEmpty = false;
        this.isNumberExceedScope = false;
        this.isNoteTypesEmpty = false;

        this.autoTaggingModel = new AutoTaggingModel();

        // get note types again
        this._store.pipe(select(fullEntryTypeSelector))
            .subscribe(res => this._onSubscribeNoteTypesList(res));
    }

    private _saveNewModel() {
        this._store.dispatch({
            type: CREATE_AUTO_TAG_MODEL,
            payload: this.autoTaggingModel
        });
    }

    /*
    * init noteTypes
    */
    private _onSubscribeNoteTypesList(item: Array<any>): void {
        this.allNoteTypes = item;
        if (this.allNoteTypes) {
            if (item && item.length > 0) {
                ArrayHelperService.sort(item, 'name');
                this.dropdownConfig.data = item.map(data => {
                    return {
                        id: data.id,
                        checked: this._isNoteTypeExisted(data.id) ? true : false,
                        name: data.name
                    };
                });
            }
        }
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === CREATE_AUTO_TAG_MODEL_SUCCESS) {
            this.autoTaggingModel.script = action.payload;
        }
    }

    private _verifyInput() {
        let result = false;
        this._verifyModelName();
        this._verifyNumber();
        this._verifyNoteTypes();

        if (!this.isModelNameEmpty && !this.isModelNameExceedMaxlength && !this.isNumberEmpty && !this.isNumberExceedScope && !this.isNoteTypesEmpty) {
            result = true;
        }
        return result;
    }

    private _verifyModelName() {
        if (!this.autoTaggingModel.modelName) {
            this.isModelNameEmpty = true;
        } else {
            this.isModelNameEmpty = false;
            if (this.autoTaggingModel.modelName.length <= 32) {
                this.isModelNameExceedMaxlength = false;
            } else {
                this.isModelNameExceedMaxlength = true;
            }
        }
    }

    private _verifyNumber() {
        if (this.autoTaggingModel.number === null) {
            this.isNumberEmpty = true;
            // only show one error message
            this.isNumberExceedScope = false;
        } else {
            this.isNumberEmpty = false;
            if (this.autoTaggingModel.number >= 10 && this.autoTaggingModel.number <= 1000) {
                this.isNumberExceedScope = false;
            } else {
                this.isNumberExceedScope = true;
            }
        }
    }

    private _verifyNoteTypes() {
        if (!this.autoTaggingModel.noteTypes) {
            this.isNoteTypesEmpty = true;
        } else {
            this.isNoteTypesEmpty = false;
        }
    }
}
